<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 pb-20" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-all mb-2 group">
            <v-icon small color="currentColor" class="transition-transform group-hover:-translate-x-1">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-black uppercase tracking-[0.2em]">Voltar</span>
          </button>
          <h1 class="text-2xl font-bold text-slate-700 tracking-tight">{{ id ? 'Editar Frentista' : 'Cadastrar Frentista' }}</h1>
        </div>
      </div>

      <div class="space-y-6 pb-12">
        
        <!-- SEÇÃO 1: FOTO E VINCULO PROFISSIONAL -->
        <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-account-tie-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E VÍNCULO</h2>
          </div>
          
          <div class="p-8">
            <div class="flex flex-col md:flex-row gap-10 items-center md:items-start">
              <!-- PHOTO UPLOAD -->
              <div class="flex flex-col items-center gap-4">
                <div 
                  class="relative group cursor-pointer"
                  @click="$refs.fileInput.click()"
                >
                  <v-avatar size="160" class="border-4 border-slate-50 shadow-md transition-transform group-hover:scale-[1.02] bg-slate-100">
                    <img
                      :src="data.attendant_photo ? $store.state.urlImages + data.attendant_photo : require('@/assets/no-image.jpg')"
                      class="object-cover"
                    />
                  </v-avatar>
                  <div class="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <v-icon color="white">mdi-camera-plus</v-icon>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  @change="uploadImage"
                  ref="fileInput"
                  style="display: none"
                />
                <p class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Clique para alterar a foto</p>
              </div>

              <!-- BASIC FIELDS -->
              <div class="flex-1 w-full space-y-6 pt-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="md:col-span-2">
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Nome Completo</label>
                    <input 
                      type="text" 
                      v-model="data.attendant_name"
                      placeholder="EX: JOÃO DA SILVA"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                    >
                  </div>
                  
                  <div class="md:col-span-2">
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Fornecedor Vinculado</label>
                    <select
                      v-model="data.provider_id"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                    >
                      <option :value="null" disabled>SELECIONE O FORNECEDOR</option>
                      <option v-for="item in providers" :key="item.provider_id" :value="item.provider_id">
                        {{ item.provider_name }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">CPF</label>
                    <input 
                      type="text" 
                      v-model="data.attendant_cpf"
                      v-mask-cpf
                      placeholder="000.000.000-00"
                      :class="['w-full bg-slate-50 border rounded px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-1', 
                        !validCpf(data.attendant_cpf) && data.attendant_cpf ? 'border-red-300 ring-red-300' : 'border-slate-200 focus:border-orange-500 focus:ring-orange-500']"
                    >
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Data de Nascimento</label>
                    <input 
                      type="text" 
                      v-model="data.attendant_birth"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SEÇÃO 2: DADOS PESSOAIS E CONTATO -->
        <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-card-account-details-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS PESSOAIS E CONTATO</h2>
          </div>
          
          <div class="p-8">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <!-- PESSOAIS -->
              <div>
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Sexo</label>
                <select
                  v-model="data.attendant_genre"
                  class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                >
                  <option value="">SELECIONE</option>
                  <option v-for="item in ['Masculino','Feminino','Outros']" :key="item" :value="item">{{ item }}</option>
                </select>
              </div>
              <div>
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Estado Civil</label>
                <select
                  v-model="data.attendant_marital_status"
                  class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                >
                  <option value="">SELECIONE</option>
                  <option v-for="item in ['Casado','Solteiro','Viúvo','União Estável']" :key="item" :value="item">{{ item }}</option>
                </select>
              </div>
              <div>
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Escolaridade</label>
                <select
                  v-model="data.attendant_education_level"
                  class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                >
                  <option value="">SELECIONE</option>
                  <option v-for="item in ['Fundamental Incompleto','Fundamental Completo','Ensino Médio Incompleto','Ensino Médio Completo','Ensino Superior Incompleto','Ensino Superior Completo','Mestrado','Doutorado','Pós-Doutorado']" :key="item" :value="item">{{ item }}</option>
                </select>
              </div>

              <!-- CONTATOS -->
              <div class="md:col-span-1">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">E-mail</label>
                <input type="email" v-model="data.attendant_mail" placeholder="EX@MAIL.COM" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
              <div>
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Celular</label>
                <input type="text" v-mask-phone.br v-model="data.attendant_cel" placeholder="(00) 00000-0000" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors">
              </div>
              <div>
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Telefone</label>
                <input type="text" v-mask-phone.br v-model="data.attendant_tel" placeholder="(00) 0000-0000" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors">
              </div>
            </div>
          </div>
        </div>

        <!-- SEÇÃO 3: LOCALIZAÇÃO -->
        <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-map-marker-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ENDEREÇO E LOCALIZAÇÃO</h2>
          </div>
          
          <div class="p-8">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div class="md:col-span-3">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">CEP</label>
                <div class="relative">
                  <input 
                    type="text" 
                    v-mask-cep v-model="data.attendant_postal_code" 
                    @blur="getCep(data.attendant_postal_code)"
                    placeholder="00.000-000"
                    class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                  >
                  <v-icon small class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">mdi-magnify</v-icon>
                </div>
              </div>
              <div class="md:col-span-7">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Logradouro</label>
                <input type="text" v-model="data.attendant_street" placeholder="RUA / AVENIDA" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
              <div class="md:col-span-2">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Número</label>
                <input type="text" v-model="data.attendant_number" placeholder="123" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>

              <div class="md:col-span-4">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Complemento</label>
                <input type="text" v-model="data.attendant_complement" placeholder="APTO / BLOCO" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
              <div class="md:col-span-3">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Bairro</label>
                <input type="text" v-model="data.attendant_district" placeholder="CENTRO" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
              <div class="md:col-span-3">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Cidade</label>
                <input type="text" v-model="data.attendant_city" placeholder="CIDADE" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
              <div class="md:col-span-2">
                <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">UF</label>
                <input type="text" v-model="data.attendant_state" placeholder="UF" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase">
              </div>
            </div>
          </div>

          <!-- FOOTER INTEGRADO -->
          <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
            <button 
              @click="$router.back()"
              class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
            >
              Cancelar
            </button>
            <div class="flex-none">
              <button
                @click="validate"
                :disabled="loader || !validCpf(data.attendant_cpf)"
                class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
              >
                <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- NOTIFICAÇÕES -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="3000"
        rounded="lg"
        elevation="24"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white" small>
            {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    providers: [],
    fieldRules: [v => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success"
    },
    data: {
      attendant_id: null,
      attendant_name: "",
      attendant_photo: "",
      attendant_cpf: "",
      attendant_birth: "",
      attendant_genre: "",
      attendant_education_level: "",
      attendant_marital_status: "",
      attendant_mail: "",
      attendant_cel: "",
      attendant_tel: "",
      attendant_postal_code: "",
      attendant_street: "",
      attendant_number: "",
      attendant_complement: "",
      attendant_district: "",
      attendant_city: "",
      attendant_state: "",
      provider_id: null
    }
  }),
  created() {
    this.$http.get("/providers").then(r => (this.providers = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/attendants/${this.id}`).then(r => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validCpf(cpf) {
      cpf = cpf.replace(/\D/g, "");
      if (cpf.toString().length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function(j) {
        var soma = 0,
          r;
        cpf
          .split(/(?=)/)
          .splice(0, j)
          .forEach(function(e, i) {
            soma += parseInt(e) * (j + 2 - (i + 1));
          });
        r = soma % 11;
        r = r < 2 ? 0 : 11 - r;
        if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      if (!this.data.attendant_name || !this.data.provider_id || !this.data.attendant_cpf) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: Preencha o Nome, Fornecedor e CPF!";
        return;
      }
      this.createOrUpdate();
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/attendants/${this.id}`, this.data)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/attendants-manager");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/attendants", this.data)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push("/attendants-manager");
            }, 1500);
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      // console.log(event.target.files[0]);
      this.$http
        .post("/uploads", fd)
        .then(r => {
          this.data.attendant_photo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch(e => {
          alert(
            "Erro ao realizar upload da imagem tente novamente! " + e.message
          );
          this.$store.dispatch("setLoader", false);
          event.target.files = [];
        });
    },
    getCep(cep) {
      if (cep.length == 10) {
        this.loading = true;
        cep = cep.replace("-", "");
        cep = cep.replace(".", "");
        this.$http
          .get("https://viacep.com.br/ws/" + cep + "/json/")
          .then(response => {
            this.data.attendant_street = response.data.logradouro;
            this.data.attendant_district = response.data.bairro;
            this.data.attendant_city = response.data.localidade;
            this.data.attendant_state = response.data.uf;
            this.loading = false;
          })
          .catch(e => {
            this.message.snacshow = true;
            this.message.type = "warning";
            this.message.text = "Atencão cep não encontrado!" + e;
            this.loading = false;
          });
      }
    }
  }
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>